import { captureException } from "@storytell/analytics";
import { dispatchGlobalEvent, isNetworkError } from "@storytell/events";
import { err, ok, type Result } from "neverthrow";
import type { z } from "zod";
import type { RequestBuilder } from "./requestBuilder";

type StorytellClientConfig = {
  getProjectId?: () => string | undefined;
  getOrgId?: () => string | undefined;
};

export let globalConfig: StorytellClientConfig = {};

export const configureStorytellClient = (config: StorytellClientConfig) => {
  globalConfig = config;
};

const FETCH_ERROR_PREFIX = "Failed to fetch: ";

/**
 * The server's own text from a refused request's error message, without the prefix this
 * client puts in front of it. Any other message comes back unchanged.
 */
export const serverMessage = (message: string): string =>
  message.startsWith(FETCH_ERROR_PREFIX) ? message.slice(FETCH_ERROR_PREFIX.length) : message;

type ParsedErrorBody = {
  code?: string;
  message?: string;
};

const parseErrorBody = (errorBody: unknown): ParsedErrorBody => {
  let parsed: unknown = errorBody;
  if (typeof errorBody === "string") {
    try {
      parsed = JSON.parse(errorBody);
    } catch {
      return { message: errorBody };
    }
  }

  if (typeof parsed === "object" && parsed !== null) {
    const result: ParsedErrorBody = {};

    if ("code" in parsed && (typeof parsed.code === "string" || typeof parsed.code === "number") && parsed.code) {
      result.code = String(parsed.code);
    }
    if ("humane" in parsed && typeof parsed.humane === "string" && parsed.humane) {
      result.message = parsed.humane;
    } else if ("error" in parsed && typeof parsed.error === "string" && parsed.error) {
      result.message = parsed.error;
    } else if ("message" in parsed && typeof parsed.message === "string" && parsed.message) {
      result.message = parsed.message;
    }

    return result;
  }

  return {};
};

async function executeRequest<ResponseSchema extends z.ZodType | undefined>(
  request: Request,
  responseSchema: ResponseSchema,
  params: unknown,
  body: unknown,
  signal?: AbortSignal
): Promise<Result<ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined, Error>> {
  try {
    const init: RequestInit = {};
    if (signal) init.signal = signal;
    const response = await fetch(request, init);
    if (!response.ok) {
      let errorBody: unknown = null;
      try {
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          errorBody = await response.json();
        } else {
          errorBody = await response.text();
        }
      } catch {
        // Ignore errors reading response body
      }

      const { code: errorCode, message: parsedMessage } = parseErrorBody(errorBody);

      let errorMessage = response.statusText;
      if (parsedMessage) {
        errorMessage = parsedMessage;
      } else if (errorCode) {
        errorMessage = `${response.statusText} (${errorCode})`;
      }

      // Statuses callers handle as ordinary outcomes: out-of-scope reads, rate limits, and
      // state conflicts (a steer or approval that lost a race with the turn).
      if (![404, 409, 429].includes(response.status)) {
        captureException(new Error(`storytellClient: Failed to fetch: ${errorMessage}`), {
          errorType: "storytellClient",
          status: response.status,
          statusText: response.statusText,
          requestUrl: request.url,
          body,
          params,
          errorBody,
        });
        console.error(`Failed to fetch: ${response.status} ${response.statusText}`, {
          url: request.url,
          errorBody,
          errorMessage,
        });
      }

      const error = new Error(`${FETCH_ERROR_PREFIX}${errorMessage}`);
      const errWithMeta = error as Error & { code?: string; status?: number };
      errWithMeta.code = errorCode;
      errWithMeta.status = response.status;
      return err(error);
    }

    if (!responseSchema) {
      return ok(undefined as ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined);
    }

    const responseBody: unknown = await response.json();
    const parsed = responseSchema.safeParse(responseBody);

    if (!parsed.success) {
      const error = new Error(`Failed to parse response: ${parsed.error.message}`);
      captureException(error, { errorType: "storytellClient", requestUrl: request.url, responseBody });
      console.error(error);
      return err(error);
    }

    return ok(parsed.data as ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined);
  } catch (error) {
    // Caller-initiated cancellation: propagate without logging or Sentry
    // would drown real errors. Accept both DOMException (browser fetch) and
    // Error (polyfills/tests) since both surface AbortError.
    if ((error instanceof DOMException || error instanceof Error) && error.name === "AbortError") {
      return err(error);
    }
    console.error(error);
    if (isNetworkError(error)) {
      dispatchGlobalEvent("connectivity-api-error", {
        error: String(error),
        code: undefined,
      });
    } else {
      captureException(error, { errorType: "storytellClient", requestUrl: request.url });
    }
    return err(error as Error);
  }
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

async function storytellFetch<
  PathParamsSchema extends z.ZodType | undefined = undefined,
  BodySchema extends z.ZodType | undefined = undefined,
  ResponseSchema extends z.ZodType | undefined = undefined,
>(
  builder: RequestBuilder<PathParamsSchema, BodySchema, ResponseSchema>,
  params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined,
  body: BodySchema extends z.ZodType ? z.infer<BodySchema> : undefined,
  options?: { signal?: AbortSignal; projectIdOverride?: string; orgIdOverride?: string }
): Promise<Result<ResponseSchema extends z.ZodType ? z.infer<ResponseSchema> : undefined, Error>> {
  const projectId = options?.projectIdOverride ?? globalConfig.getProjectId?.();
  const organizationId = options?.orgIdOverride ?? globalConfig.getOrgId?.();

  const config = builder.build(params, body, { projectId, organizationId });
  if (config.isErr()) {
    captureException(config.error);
    return err(config.error);
  }

  const { request, responseSchema, includeProjectId } = config.value;

  const RATE_LIMIT_RETRY_DELAYS_MS = [1000, 2000, 4000];

  let result = await executeRequest(request, responseSchema, params, body, options?.signal);

  const errorWithMeta = result.isErr() ? (result.error as Error & { status?: number }) : null;
  if (result.isErr() && errorWithMeta?.status === 429) {
    for (let i = 0; i < RATE_LIMIT_RETRY_DELAYS_MS.length; i++) {
      const delay = RATE_LIMIT_RETRY_DELAYS_MS[i];
      if (delay === undefined) break;
      await sleep(delay);
      const retryConfig = builder.build(params, body, { projectId, organizationId });
      if (retryConfig.isErr()) break;
      result = await executeRequest(retryConfig.value.request, responseSchema, params, body, options?.signal);
      if (result.isOk()) return result;
      const retryError = result.error as Error & { status?: number };
      if (retryError.status !== 429) break;
    }
  }

  if (result.isErr() && includeProjectId) {
    // A request that never got a response carries the DOMException itself, whose legacy
    // numeric code (20 for an abort) shares the field name with the backend's string code.
    const currentError = result.error as Error & { status?: number; code?: unknown };
    // 404 means the X-Project-Id header no longer resolves to a live project
    // (deleted, or the URL is stale); treat it like 401/403. A 401 the bearer
    // check raised says the token is stale, not that the project is gone.
    const tokenRejected = typeof currentError.code === "string" && currentError.code.startsWith("auth.bearer.");
    if (!tokenRejected && (currentError.status === 403 || currentError.status === 401 || currentError.status === 404)) {
      const projectIdForEvent = (params as { projectId?: string } | undefined)?.projectId ?? projectId;
      if (projectIdForEvent) {
        dispatchGlobalEvent("project-access-denied", { projectId: projectIdForEvent });
      }
    }
  } else if (includeProjectId) {
    const projectIdForEvent = (params as { projectId?: string } | undefined)?.projectId ?? projectId;
    if (projectIdForEvent) {
      dispatchGlobalEvent("project-access-ok", { projectId: projectIdForEvent });
    }
  }

  return result;
}

export const useStorytellClient = () => {
  return {
    fetch: storytellFetch,
  };
};
